/**
 * api.js — all Supabase database operations in one place.
 *
 * Rules:
 *  - Every function returns the raw Supabase promise (caller handles .data / .error)
 *  - No state, no side-effects, no React here — just data operations
 *  - Components call these instead of touching supabase directly
 *
 * Adding something new? Put it in the right section below and export it.
 */

import { supabase } from './supabase'

// ── PROJECTS ──────────────────────────────────────────────────────────────────

export const fetchProjects = () =>
  supabase.from('projects').select('*, aircraft(*)').is('deleted_at', null).order('arrival_date', { ascending: true })

export const fetchDeletedProjects = () =>
  supabase.from('projects').select('*, aircraft(*)').not('deleted_at', 'is', null).order('deleted_at', { ascending: false })

export const createProject = (data) =>
  supabase.from('projects').insert(data).select().single()

export const updateProject = (id, data) =>
  supabase.from('projects').update(data).eq('id', id)

export const deleteProject = async (id) => {
  const { data: proj } = await supabase.from('projects').select('work_order').eq('id', id).single()
  if (proj?.work_order) {
    await supabase.from('customer_item_approvals').delete().eq('work_order', proj.work_order)
  }
  return supabase.from('projects').update({ deleted_at: new Date().toISOString() }).eq('id', id)
}

export const restoreProject = (id) =>
  Promise.all([
    supabase.from('projects').update({ deleted_at: null }).eq('id', id),
    supabase.from('subtasks').update({ deleted_at: null }).eq('project_id', id),
  ])

export const purgeProject = async (id) => {
  const { data: subtaskRows } = await supabase.from('subtasks').select('id').eq('project_id', id)
  const subtaskIds = (subtaskRows || []).map(r => r.id)
  if (subtaskIds.length > 0) {
    await supabase.from('subtask_technicians').delete().in('subtask_id', subtaskIds)
  }
  await Promise.all([
    supabase.from('subtasks').delete().eq('project_id', id),
    supabase.from('project_technicians').delete().eq('project_id', id),
    supabase.from('project_technician_dates').delete().eq('project_id', id),
    supabase.from('customer_quote_requests').delete().eq('project_id', id),
    supabase.from('project_notes').delete().eq('project_id', id),
    supabase.from('comments').delete().eq('project_id', id),
    // tasks are not deleted — just unlinked so they survive the project purge
    supabase.from('tasks').update({ linked_project_id: null }).eq('linked_project_id', id),
  ])
  return supabase.from('projects').delete().eq('id', id)
}

// ── SUBTASKS ──────────────────────────────────────────────────────────────────

export const fetchSubtasks = (projectIds) =>
  projectIds.length === 0
    ? Promise.resolve({ data: [] })
    : supabase.from('subtasks').select('*').in('project_id', projectIds).is('deleted_at', null).order('sort_order', { ascending: true })

export const fetchSubtasksByProjectId = (projectId) =>
  supabase.from('subtasks').select('*').eq('project_id', projectId).is('deleted_at', null).order('sort_order', { ascending: true })

export const fetchSubtaskDatesByProject = (projectId) =>
  supabase.from('subtasks').select('id, start_date, end_date').eq('project_id', projectId).is('deleted_at', null)

export const createSubtask = (data) =>
  supabase.from('subtasks').insert(data).select().single()

export const createSubtasks = (data) =>
  supabase.from('subtasks').insert(data)

export const updateSubtask = (id, data) =>
  supabase.from('subtasks').update(data).eq('id', id)

export const deleteSubtask = (id) =>
  supabase.from('subtasks').update({ deleted_at: new Date().toISOString() }).eq('id', id)

// Hard delete — used when replacing subtasks wholesale via workflow templates
export const deleteProjectSubtasks = (projectId) =>
  supabase.from('subtasks').delete().eq('project_id', projectId)

// Mark all non-complete subtasks for a project as Complete in a single DB call
export const completeAllProjectSubtasks = (projectId) =>
  supabase
    .from('subtasks')
    .update({ status: 'Complete' })
    .eq('project_id', projectId)
    .neq('status', 'Complete')
    .is('deleted_at', null)

// ── AIRCRAFT ──────────────────────────────────────────────────────────────────

export const fetchAircraft = () =>
  supabase.from('aircraft').select('*').order('registration', { ascending: true })

export const fetchAircraftLatest = () =>
  supabase.from('aircraft').select('*').order('created_at', { ascending: false })

export const createAircraft = (data) =>
  supabase.from('aircraft').insert(data).select().single()

export const updateAircraft = (id, data) =>
  supabase.from('aircraft').update(data).eq('id', id)

export const deleteAircraft = (id) =>
  supabase.from('aircraft').delete().eq('id', id)

// ── TECHNICIANS ───────────────────────────────────────────────────────────────

export const fetchTechnicians = () =>
  supabase.from('technicians').select('*').order('last_name', { ascending: true })

export const createTechnician = (data) =>
  supabase
    .from('technicians')
    .upsert(data, { onConflict: 'first_name,last_name' })
    .select()
    .single()

export const deleteTechnician = (id) =>
  supabase.from('technicians').delete().eq('id', id)

// ── PROJECT TECHNICIANS (junction) ────────────────────────────────────────────

export const fetchProjectTechnicians = () =>
  supabase.from('project_technicians').select('*')

export const addProjectTechnician = (projectId, technicianId) =>
  supabase.from('project_technicians').upsert({ project_id: projectId, technician_id: technicianId })

export const removeProjectTechnician = (projectId, technicianId) =>
  supabase.from('project_technicians').delete()
    .eq('project_id', projectId).eq('technician_id', technicianId)

// ── PROJECT TECHNICIAN DATE RANGES ────────────────────────────────────────────

export const fetchProjectTechDates = () =>
  supabase.from('project_technician_dates').select('*').order('start_date', { ascending: true })

export const addProjectTechDate = (projectId, technicianId, startDate, endDate) =>
  supabase.from('project_technician_dates')
    .insert({ project_id: projectId, technician_id: technicianId, start_date: startDate || null, end_date: endDate || null })
    .select().single()

export const removeProjectTechDate = (id) =>
  supabase.from('project_technician_dates').delete().eq('id', id)

// ── SUBTASK TECHNICIANS (junction) ────────────────────────────────────────────

export const fetchSubtaskTechnicians = () =>
  supabase.from('subtask_technicians').select('*')

export const fetchSubtaskTechniciansBySubtask = (subtaskId) =>
  supabase.from('subtask_technicians').select('technician_id').eq('subtask_id', subtaskId)

export const deleteSubtaskTechniciansBySubtask = (subtaskId) =>
  supabase.from('subtask_technicians').delete().eq('subtask_id', subtaskId)

export const insertSubtaskTechniciansBatch = (assignments) =>
  supabase.from('subtask_technicians').insert(assignments)

export const removeSubtaskTechniciansByProjectAndTech = (subtaskIds, technicianId) => {
  if (!subtaskIds || subtaskIds.length === 0) return Promise.resolve({ data: null, error: null })
  return supabase.from('subtask_technicians')
    .delete()
    .in('subtask_id', subtaskIds)
    .eq('technician_id', technicianId)
}

// ── CALLOUTS ──────────────────────────────────────────────────────────────────

export const fetchCallouts = () =>
  supabase.from('callouts').select('*').order('date', { ascending: true })

export const createCallout = (data) =>
  supabase.from('callouts').insert(data).select().single()

export const updateCallout = (id, data) =>
  supabase.from('callouts').update(data).eq('id', id)

export const deleteCallout = (id) =>
  supabase.from('callouts').delete().eq('id', id)

// ── WORKFLOW TEMPLATES ────────────────────────────────────────────────────────

export const fetchAllWorkflowTemplates = () =>
  supabase.from('workflow_templates').select('*').order('created_at', { ascending: false })

export const fetchWorkflowTemplates = (sourceProjectId) =>
  supabase.from('workflow_templates').select('*')
    .eq('source_project_id', sourceProjectId)
    .order('created_at', { ascending: false })

export const fetchLatestWorkflowTemplate = (sourceProjectId) =>
  supabase.from('workflow_templates').select('*')
    .eq('source_project_id', sourceProjectId)
    .order('created_at', { ascending: false })
    .limit(1)

export const createWorkflowTemplate = (data) =>
  supabase.from('workflow_templates').insert(data).select().single()

export const updateWorkflowTemplate = (id, data) =>
  supabase.from('workflow_templates').update(data).eq('id', id)

export const deleteWorkflowTemplate = (id) =>
  supabase.from('workflow_templates').delete().eq('id', id)

// ── AUDIT LOG ─────────────────────────────────────────────────────────────────

export const insertAuditLog = (data) =>
  supabase.from('audit_log').insert(data)

export const fetchAuditLog = (projectId) =>
  supabase.from('audit_log').select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })

// ── WORKFLOW SNAPSHOTS ────────────────────────────────────────────────────────

export const upsertWorkflowSnapshot = (projectId, state) =>
  supabase.from('workflow_snapshots').upsert(
    { project_id: projectId, snapshot_date: new Date().toISOString().slice(0, 10), state, updated_at: new Date().toISOString() },
    { onConflict: 'project_id,snapshot_date', ignoreDuplicates: false }
  )

export const fetchWorkflowSnapshots = (projectId) =>
  supabase.from('workflow_snapshots')
    .select('snapshot_date, state, updated_at')
    .eq('project_id', projectId)
    .order('snapshot_date', { ascending: false })

// ── FLIGHT LEGS ───────────────────────────────────────────────────────────────

export const fetchFlightLegs = () =>
  supabase.from('flight_legs').select('*').order('depart_dt', { ascending: true })

